import { ReactElement, useCallback } from 'react';

import {
   Button,
   ButtonVariant,
   StyledFlex,
   Text,
   Tooltip,
   TooltipPlacement,
   TooltipTrigger,
} from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import { StyledEmergencyButton } from '../shared/StyledEmergencyButton';

import { EmergencyNotificationVariant, useEmergencyNotification } from './useEmergencyNotification';
import { StyledEmergencyNotificationContent } from './EmergencyNotificationTooltip.styled';
import { EmergencyNotificationCountdown } from './EmergencyNotificationCountdown';

type EmergencyNotificationTooltipProps = Readonly<{
   children: ReactElement;
   variant: EmergencyNotificationVariant;
}>;

export const EmergencyNotificationTooltip = (props: EmergencyNotificationTooltipProps): JSX.Element => {
   const { trigger, cancel, state } = useEmergencyNotification();

   const content = useCallback(
      (hideTooltip: VoidFunction) => {
         const triggerAndHide = (): void => {
            trigger(props.variant);
            hideTooltip();
         };

         const cancelAndHide = (): void => {
            cancel();
            hideTooltip();
         };

         if (state.type !== 'idle') {
            return (
               <StyledEmergencyNotificationContent>
                  <Text testId={'emergencyNotificationAccepted'}>
                     <Translate>{translationKeys.emergencyNotificationAccepted}</Translate>
                  </Text>
                  <Button
                     testId={'cancel'}
                     label={translationKeys.sosCancel}
                     onClick={cancelAndHide}
                     variant={ButtonVariant.PRIMARY}
                     fullLength
                     compact
                  />
               </StyledEmergencyNotificationContent>
            );
         }

         return (
            <StyledEmergencyNotificationContent>
               <EmergencyNotificationCountdown call={triggerAndHide} close={cancelAndHide} />
               <StyledFlex gap={'23px'}>
                  <StyledEmergencyButton
                     testId={'cancel'}
                     label={translationKeys.sosCancelWithEscapeKey}
                     onClick={cancelAndHide}
                     variant={ButtonVariant.SECONDARY}
                     compact
                  />
                  <StyledEmergencyButton
                     testId={'sendImmediately'}
                     label={translationKeys.sosSendImmediately}
                     onClick={triggerAndHide}
                     variant={ButtonVariant.PRIMARY}
                     compact
                  />
               </StyledFlex>
            </StyledEmergencyNotificationContent>
         );
      },
      [trigger, cancel, state, props.variant]
   );

   const triggerType = state.type === 'idle' ? TooltipTrigger.CLICK : TooltipTrigger.HOVER;

   return (
      <Tooltip
         testId={'sos'}
         content={content}
         trigger={triggerType}
         maxWidth={'321px'}
         placement={TooltipPlacement.BOTTOM}
         hideOnOutsideClick={false}
      >
         {props.children}
      </Tooltip>
   );
};
